import type {
  ApiErrorBody,
  AuthenticatedUser,
  BulkReview,
  CatalogCategoryOption,
  CatalogHealth,
  CategoryHealth,
  CatalogImpact,
  ConnectionTestResult,
  CreateTenantRequest,
  CreateUserRequest,
  GenerationPreview,
  GroupedSuggestionList,
  LoginResponse,
  Paginated,
  ProductDetail,
  ProductListItem,
  ReviewDecision,
  RunSummary,
  StartAudit,
  StartGeneration,
  StartIngestion,
  StartWrite,
  StyleGuide,
  SuggestionListItem,
  SuggestionStats,
  TenantSummary,
  TenantLlmConfig,
  UpdateTenantLlmConfig,
  UpdateTenantRequest,
  UpdateUserRequest,
  UserSummary,
  WriteOperationProductGroup,
  WritePreview,
  AppModule,
} from '@vce/shared';
import { clearSession, loadSession, saveSession } from './session';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type QueryValue = string | number | boolean | readonly string[] | undefined;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  /** Evita laço infinito quando a própria renovação devolve 401. */
  skipRefresh?: boolean;
  raw?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE_URL}/api${path}`, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value === undefined || value === '') continue;
    url.searchParams.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }

  const session = loadSession();
  const response = await fetch(url.toString(), {
    method: options.method ?? 'GET',
    headers: {
      Accept: options.raw ? 'application/xml' : 'application/json',
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(session ? { Authorization: `Bearer ${session.accessToken}` } : {}),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  // Sessão expirada: renova uma vez e repete, para o revisor não perder o
  // trabalho no meio de uma fila longa.
  if (response.status === 401 && session && !options.skipRefresh) {
    const renewed = await tryRefresh(session.refreshToken);
    if (renewed) return request<T>(path, { ...options, skipRefresh: true });
    clearSession();
    window.location.href = '/login';
    throw new ApiError(401, 'Sessão expirada');
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(response.status, body?.message ?? `Erro ${response.status}`, body?.details);
  }

  if (response.status === 204) return undefined as T;
  if (options.raw) return (await response.text()) as T;
  return (await response.json()) as T;
}

/** Download autenticado (attachment) com nome do Content-Disposition quando presente. */
async function downloadAttachment(
  path: string,
  query: Record<string, QueryValue> = {},
  fallbackFilename: string,
): Promise<void> {
  const url = new URL(`${BASE_URL}/api${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') continue;
    url.searchParams.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }

  const session = loadSession();
  let response = await fetch(url.toString(), {
    headers: {
      ...(session ? { Authorization: `Bearer ${session.accessToken}` } : {}),
    },
  });

  if (response.status === 401 && session) {
    const renewed = await tryRefresh(session.refreshToken);
    if (!renewed) {
      clearSession();
      window.location.href = '/login';
      throw new ApiError(401, 'Sessão expirada');
    }
    const renewedSession = loadSession();
    response = await fetch(url.toString(), {
      headers: {
        ...(renewedSession ? { Authorization: `Bearer ${renewedSession.accessToken}` } : {}),
      },
    });
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(response.status, body?.message ?? `Erro ${response.status}`, body?.details);
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}

async function tryRefresh(refreshToken: string): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) return false;
    saveSession((await response.json()) as LoginResponse);
    return true;
  } catch {
    return false;
  }
}

export const api = {
  auth: {
    login: (email: string, password: string) =>
      request<LoginResponse>('/auth/login', { method: 'POST', body: { email, password } }),
    logout: (refreshToken: string) =>
      request<void>('/auth/logout', { method: 'POST', body: { refreshToken }, skipRefresh: true }),
    me: () => request<AuthenticatedUser>('/auth/me'),
  },

  users: {
    list: () => request<UserSummary[]>('/users'),
    create: (body: CreateUserRequest) => request<UserSummary>('/users', { method: 'POST', body }),
    update: (userId: string, body: UpdateUserRequest) =>
      request<UserSummary>(`/users/${userId}`, { method: 'PATCH', body }),
    replaceTenants: (userId: string, tenantIds: string[]) =>
      request<UserSummary>(`/users/${userId}/tenants`, { method: 'PUT', body: { tenantIds } }),
    replaceModules: (userId: string, modules: AppModule[]) =>
      request<UserSummary>(`/users/${userId}/modules`, { method: 'PUT', body: { modules } }),
  },

  tenants: {
    list: () => request<TenantSummary[]>('/tenants'),
    get: (tenantId: string) => request<TenantSummary>(`/tenants/${tenantId}`),
    create: (body: CreateTenantRequest) =>
      request<TenantSummary>('/tenants', { method: 'POST', body }),
    update: (tenantId: string, body: UpdateTenantRequest) =>
      request<TenantSummary>(`/tenants/${tenantId}`, { method: 'PATCH', body }),
    testConnection: (tenantId: string) =>
      request<ConnectionTestResult>(`/tenants/${tenantId}/test-connection`, { method: 'POST' }),
    getStyleGuide: (tenantId: string) => request<StyleGuide>(`/tenants/${tenantId}/style-guide`),
    saveStyleGuide: (tenantId: string, body: StyleGuide) =>
      request<StyleGuide>(`/tenants/${tenantId}/style-guide`, { method: 'PUT', body }),
    getLlmConfig: (tenantId: string) =>
      request<TenantLlmConfig>(`/tenants/${tenantId}/llm-config`),
    saveLlmConfig: (tenantId: string, body: UpdateTenantLlmConfig) =>
      request<TenantLlmConfig>(`/tenants/${tenantId}/llm-config`, { method: 'PUT', body }),
  },

  catalog: {
    health: (tenantId: string) => request<CatalogHealth>(`/tenants/${tenantId}/dashboard`),
    impact: (tenantId: string, query: { days?: 7 | 30 } = {}) =>
      request<CatalogImpact>(`/tenants/${tenantId}/dashboard/impact`, { query }),
    exportImpact: (
      tenantId: string,
      query: { days?: 7 | 30; format?: 'json' | 'csv' } = {},
    ) =>
      downloadAttachment(
        `/tenants/${tenantId}/dashboard/impact/export`,
        query,
        `catalyze-impact.${query.format === 'csv' ? 'csv' : 'json'}`,
      ),
    categories: (tenantId: string) =>
      request<CategoryHealth[]>(`/tenants/${tenantId}/dashboard/categories`),
    categoryTree: (tenantId: string) =>
      request<CatalogCategoryOption[]>(`/tenants/${tenantId}/catalog/categories/tree`),
    products: (tenantId: string, query: Record<string, QueryValue>) =>
      request<Paginated<ProductListItem>>(`/tenants/${tenantId}/products`, { query }),
    product: (tenantId: string, productId: number) =>
      request<ProductDetail>(`/tenants/${tenantId}/products/${productId}`),
    jsonLd: (tenantId: string, productId: number) =>
      request<Record<string, unknown>>(`/tenants/${tenantId}/products/${productId}/jsonld`),
    rules: (tenantId: string) =>
      request<Array<{ id: string; field: string | null; severity: string; description: string }>>(
        `/tenants/${tenantId}/rules`,
      ),
  },

  suggestions: {
    list: (tenantId: string, query: Record<string, QueryValue>) =>
      request<Paginated<SuggestionListItem>>(`/tenants/${tenantId}/suggestions`, { query }),
    listGrouped: (tenantId: string, query: Record<string, QueryValue>) =>
      request<GroupedSuggestionList>(`/tenants/${tenantId}/suggestions/grouped`, { query }),
    stats: (tenantId: string) => request<SuggestionStats>(`/tenants/${tenantId}/suggestions/stats`),
    review: (tenantId: string, suggestionId: string, body: ReviewDecision) =>
      request<void>(`/tenants/${tenantId}/suggestions/${suggestionId}`, { method: 'PATCH', body }),
    bulkReview: (tenantId: string, body: BulkReview) =>
      request<{ updated: number; skipped: string[] }>(
        `/tenants/${tenantId}/suggestions/bulk-review`,
        {
          method: 'POST',
          body,
        },
      ),
  },

  runs: {
    list: (tenantId: string, kind?: string) =>
      request<RunSummary[]>(`/tenants/${tenantId}/runs`, { query: { kind } }),
    startIngestion: (tenantId: string, body: StartIngestion) =>
      request<RunSummary>(`/tenants/${tenantId}/runs/ingestion`, { method: 'POST', body }),
    startAudit: (tenantId: string, body: StartAudit) =>
      request<RunSummary>(`/tenants/${tenantId}/runs/audit`, { method: 'POST', body }),
    startGeneration: (tenantId: string, body: StartGeneration) =>
      request<RunSummary>(`/tenants/${tenantId}/runs/generation`, { method: 'POST', body }),
    startWrite: (tenantId: string, body: StartWrite) =>
      request<RunSummary>(`/tenants/${tenantId}/runs/write`, { method: 'POST', body }),
    writePreview: (tenantId: string, query?: { limit?: number; q?: string }) =>
      request<WritePreview>(`/tenants/${tenantId}/runs/write/preview`, { query }),
    generationPreview: (
      tenantId: string,
      query: { fields: string[]; onlyWithFindings?: boolean; limit?: number; q?: string },
    ) => request<GenerationPreview>(`/tenants/${tenantId}/runs/generation/preview`, { query }),
    pause: (tenantId: string, runId: string) =>
      request<RunSummary>(`/tenants/${tenantId}/runs/${runId}/pause`, { method: 'POST' }),
    resume: (tenantId: string, runId: string) =>
      request<RunSummary>(`/tenants/${tenantId}/runs/${runId}/resume`, { method: 'POST' }),
  },

  writeOperations: {
    list: (tenantId: string, query: Record<string, QueryValue>) =>
      request<Paginated<WriteOperationProductGroup>>(`/tenants/${tenantId}/write-operations`, {
        query,
      }),
    rollback: (tenantId: string, id: string) =>
      request<{ queued: true }>(`/tenants/${tenantId}/write-operations/${id}/rollback`, {
        method: 'POST',
      }),
  },
};
